import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useGenericEventsLogger } from '@wix/data-tools-generic-events';
import {
  Button,
  ButtonPriority,
  TextButton,
  TextButtonPriority,
} from 'wix-ui-tpa/cssVars';
import { DayPicker, UI, type DateRange } from 'react-day-picker';
import './dayPicker.global.scss';
import { classes } from './DateRangePicker.st.css';
import { Date as DateIcon } from '@wix/wix-ui-icons-common';
import { ChevronDown } from '@wix/wix-ui-icons-common/on-stage';
import { MobileSheet } from '../MobileSheet';
import { RentalsListDataHooks } from '../dataHooks';
import { isSingleDay, toDateString } from '../../../../utils/dateRange';

export type DateRangePickerProps = {
  value: [string, string] | null;
  /** Fired on Apply and Clear. Does not search — the bar holds it as a draft. */
  onApply: (range: [string, string] | null) => void;
  isMobile: boolean;
  layer: HTMLElement | null;
};

/** `YYYY-MM-DD` → a short, locale-aware label (e.g. "Jun 5"). */
function formatDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function toDayPickerRange(
  value: [string, string] | null,
): DateRange | undefined {
  if (!value) {
    return undefined;
  }
  return {
    from: new Date(`${value[0]}T00:00:00`),
    to: new Date(`${value[1]}T00:00:00`),
  };
}

/** A single picked day is valid: it commits as a `[day, day]` range. */
function fromDayPickerRange(
  draft: DateRange | undefined,
): [string, string] | null {
  if (!draft?.from) {
    return null;
  }
  const start = toDateString(draft.from);
  return [start, draft.to ? toDateString(draft.to) : start];
}

export const DateRangePicker: FC<DateRangePickerProps> = ({
  value,
  onApply,
  isMobile,
  layer,
}) => {
  const { t } = useTranslation();
  const logger = useGenericEventsLogger();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(() =>
    toDayPickerRange(value),
  );

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const label = value
    ? isSingleDay(value)
      ? formatDate(value[0])
      : `${formatDate(value[0])} - ${formatDate(value[1])}`
    : t('widget.servicesList.searchBar.datePlaceholder');

  const open = () => {
    setDraft(toDayPickerRange(value));
    setIsOpen(true);
  };

  const calendar = (
    <DayPicker
      mode="range"
      numberOfMonths={isMobile ? 1 : 2}
      selected={draft}
      onSelect={setDraft}
      disabled={{ before: today }}
      excludeDisabled
      styles={{ [UI.Months]: { display: 'flex', gap: '32px' } }}
    />
  );

  const footer = (
    <>
      {/* Leaves the calendar open, so a mistaken clear can be redone. */}
      <TextButton
        priority={TextButtonPriority.primary}
        className={classes.clear}
        onClick={() => {
          logger.clickCta({ componentName: 'clear_date' });
          setDraft(undefined);
          onApply(null);
        }}
      >
        {t('widget.servicesList.searchBar.clearDate')}
      </TextButton>
      <Button
        priority={ButtonPriority.primary}
        className={classes.apply}
        disabled={!draft?.from}
        data-hook={RentalsListDataHooks.searchDateApply}
        onClick={() => {
          // Apply with nothing picked must not clear the selection.
          const range = fromDayPickerRange(draft);
          if (range) {
            logger.userInput({
              componentName: 'date_filter',
              inputNewValue: range.join(','),
              inputPrevValue: value ? value.join(',') : '',
            });
            onApply(range);
          }
          setIsOpen(false);
        }}
      >
        {t('widget.servicesList.searchBar.applyDate')}
      </Button>
    </>
  );

  return (
    <div className={classes.root}>
      <button
        type="button"
        className={classes.trigger}
        data-hook={RentalsListDataHooks.searchDate}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        aria-expanded={isOpen}
      >
        <DateIcon className={classes.icon} />
        <span className={value ? classes.labelActive : classes.label}>
          {label}
        </span>
        <ChevronDown className={classes.chevron} />
      </button>

      {isOpen && isMobile ? (
        <MobileSheet
          layer={layer}
          label={t('widget.servicesList.searchBar.datePlaceholder')}
          onClose={() => setIsOpen(false)}
          footer={footer}
          data-hook={RentalsListDataHooks.searchDatePopover}
        >
          <div className={classes.sheetCalendar}>{calendar}</div>
        </MobileSheet>
      ) : null}

      {isOpen && !isMobile ? (
        <>
          {/* Click-away layer; the calendar sits above it. */}
          <div className={classes.backdrop} onClick={() => setIsOpen(false)} />
          <div
            className={classes.popover}
            data-hook={RentalsListDataHooks.searchDatePopover}
          >
            {calendar}
            <div className={classes.footer}>{footer}</div>
          </div>
        </>
      ) : null}
    </div>
  );
};
